/** Typed wrappers for the admin source-snapshot checklist workflow. */

import { useMemo } from 'react';
import type { ChecklistMarketplaceProfile } from '@tcvs/shared';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';
import type {
  ChecklistIssue,
  ChecklistIssueDiagnosis,
  ChecklistIssuesResponse,
  ChecklistIssueStatus,
  ReviewChecklistProduct,
} from '../shared/types';

/** `GET /review-checklists` response — compact directory of ingested products. */
export interface CurrentChecklistsResponse {
  items: ReviewChecklistProduct[];
  truncated: boolean;
  nextToken?: string;
}

/** `POST /checklists/upload-url` response — the presigned S3 POST target. */
export interface ChecklistUploadUrlResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
}

export interface ChecklistSourceDiffCounts {
  adds: number;
  updates: number;
  unchanged: number;
  sourceMissing: number;
}

export interface ChecklistSourceQualityWarnings {
  missingCardNumber: number;
  missingName: number;
  samples: { sourceItemId: string; field: 'number' | 'name' }[];
}

export interface ChecklistSourcePreview {
  sourceSystem: string;
  sourceTargetId: string;
  product: {
    category: string;
    year: string;
    brand: string;
    product: string;
  };
  marketplace?: ChecklistMarketplaceProfile;
  fingerprint: string;
  rosterCount: number;
  parallelDefinitionCount: number;
  subsetDefinitionCount: number;
  qualityWarnings?: ChecklistSourceQualityWarnings;
  diff: ChecklistSourceDiffCounts;
  parallelDiff: ChecklistSourceDiffCounts;
  subsetDiff: ChecklistSourceDiffCounts;
  replaceExisting: boolean;
  replacement?: ChecklistSourceReplacement;
  destructiveChanges: boolean;
}

export interface ChecklistSourceReplacementProduct {
  checklistId: string;
  category: string;
  year: string;
  brand: string;
  product: string;
  rosterRecords: number;
}

export interface ChecklistSourceReplacement {
  token: string;
  rosterDeletes: number;
  sourceRecordDeletes: number;
  parallelDeletes: number;
  subsetDeletes: number;
  productDirectoryDeletes: number;
  duplicateProducts: ChecklistSourceReplacementProduct[];
  ownershipConflicts: number;
}

export interface ChecklistSourceIngestSummary {
  fingerprint: string;
  rosterCount: number;
  parallelDefinitionCount: number;
  subsetDefinitionCount: number;
  diff: ChecklistSourceDiffCounts;
  parallelDiff: ChecklistSourceDiffCounts;
  subsetDiff: ChecklistSourceDiffCounts;
  replaceExisting: boolean;
  replacement?: ChecklistSourceReplacement;
  alreadyCommitted: boolean;
}

export interface ChecklistSourceImportJob {
  jobId: string;
  status: 'QUEUED' | 'RETRYING' | 'RUNNING' | 'PREPARING' | 'COMPLETED' | 'FAILED';
  sourceSystem: string;
  sourceTargetId: string;
  fingerprint: string;
  rosterCount: number;
  parallelDefinitionCount: number;
  subsetDefinitionCount: number;
  progress?: { phase: string; processed: number; total: number };
  diff?: ChecklistSourceDiffCounts;
  parallelDiff?: ChecklistSourceDiffCounts;
  subsetDiff?: ChecklistSourceDiffCounts;
  replaceExisting?: boolean;
  replacement?: ChecklistSourceReplacement;
  error?: string;
}

export interface ChecklistSourceBatchItem {
  key: string;
  fingerprint: string;
  sourceTargetId: string;
  rosterCount: number;
  parallelDefinitionCount: number;
  subsetDefinitionCount: number;
  qualityWarnings?: Omit<ChecklistSourceQualityWarnings, 'samples'>;
}

export interface ChecklistSourceImportBatch {
  batchId: string;
  status: 'QUEUED' | 'RUNNING' | 'RETRYING' | 'COMPLETED' | 'COMPLETED_WITH_ERRORS' | 'FAILED';
  fingerprint: string;
  total: number;
  queued: number;
  /** Existing running count includes jobs waiting to retry. */
  running: number;
  retrying?: number;
  retryDetails?: { sourceTargetId: string; reason: string; failedAt: string }[];
  completed: number;
  failed: number;
  rosterCount?: number;
  parallelDefinitionCount?: number;
  subsetDefinitionCount?: number;
  error?: string;
}

/** Outcome of a raw S3 presigned-POST source-snapshot upload. */
export type WorkbookUploadResult = { ok: true } | { ok: false; error: string };

/**
 * Upload a source snapshot to S3 using the presigned POST. Appends every policy
 * field first, then the file last (S3 requires `file` last), and POSTs the
 * `multipart/form-data` directly to the S3 URL. A non-2xx response or a network
 * error is surfaced as `{ ok: false }`. Never sends the file through the API.
 */
export async function uploadChecklistSnapshotToS3(
  presigned: ChecklistUploadUrlResponse,
  file: File,
): Promise<WorkbookUploadResult> {
  const form = new FormData();
  for (const [k, v] of Object.entries(presigned.fields)) {
    form.append(k, v);
  }
  form.append('file', file);
  try {
    const response = await fetch(presigned.url, { method: 'POST', body: form });
    if (!response.ok) {
      return { ok: false, error: `s3_upload_failed_${response.status}` };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export function useChecklistsApi() {
  const api = useApi();
  return useMemo(
    () => ({
      /** List the checklist products currently available to identification workflows. */
      listCurrent: (options?: {
        query?: string;
        limit?: number;
        nextToken?: string;
      }): Promise<ApiResult<CurrentChecklistsResponse>> =>
        api.request<CurrentChecklistsResponse>('/review-checklists', {
          query: {
            ...(options?.query === undefined ? {} : { q: options.query }),
            ...(options?.limit === undefined ? {} : { limit: String(options.limit) }),
            ...(options?.nextToken === undefined ? {} : { nextToken: options.nextToken }),
          },
        }),
      listIssues: (status: ChecklistIssueStatus): Promise<ApiResult<ChecklistIssuesResponse>> =>
        api.request<ChecklistIssuesResponse>('/checklists/issues', {
          method: 'GET',
          query: { status },
        }),
      setIssueStatus: (
        issueKey: string,
        status: ChecklistIssueStatus,
      ): Promise<ApiResult<{ issue: ChecklistIssue }>> =>
        api.request<{ issue: ChecklistIssue }>(
          `/checklists/issues/${encodeURIComponent(issueKey)}`,
          { method: 'PATCH', body: { status } },
        ),
      diagnoseIssueCard: (
        cardId: string,
      ): Promise<ApiResult<{ diagnosis: ChecklistIssueDiagnosis }>> =>
        api.request<{ diagnosis: ChecklistIssueDiagnosis }>(
          `/cards/${encodeURIComponent(cardId)}/checklist-resolve`,
          { method: 'POST', body: { operation: 'DIAGNOSE' } },
        ),
      saveIssueAnalysis: (
        issueKey: string,
        analysis: ChecklistIssueDiagnosis,
      ): Promise<ApiResult<{ issue: ChecklistIssue }>> =>
        api.request<{ issue: ChecklistIssue }>(
          `/checklists/issues/${encodeURIComponent(issueKey)}`,
          { method: 'PATCH', body: { analysis } },
        ),
      saveIssueReviewerGuidance: (
        issueKey: string,
        reviewerGuidance: string | null,
      ): Promise<ApiResult<{ issue: ChecklistIssue }>> =>
        api.request<{ issue: ChecklistIssue }>(
          `/checklists/issues/${encodeURIComponent(issueKey)}`,
          { method: 'PATCH', body: { reviewerGuidance } },
        ),
      /** Mint a presigned POST for a schema-version-6 JSON source snapshot. */
      uploadUrl: (ext?: 'json' | 'json.gz'): Promise<ApiResult<ChecklistUploadUrlResponse>> =>
        api.request<ChecklistUploadUrlResponse>('/checklists/upload-url', {
          method: 'POST',
          ...(ext !== undefined ? { body: { ext } } : {}),
        }),
      sourcePreview: (body: {
        key: string;
        replaceExisting?: boolean;
      }): Promise<ApiResult<ChecklistSourcePreview>> =>
        api.request<ChecklistSourcePreview>('/checklists/source-preview', {
          method: 'POST',
          body,
        }),
      sourceIngest: (body: {
        key: string;
        fingerprint: string;
        replaceExisting?: boolean;
        replacementToken?: string;
      }): Promise<ApiResult<ChecklistSourceIngestSummary | ChecklistSourceImportJob>> =>
        api.request<ChecklistSourceIngestSummary | ChecklistSourceImportJob>(
          '/checklists/source-ingest',
          {
            method: 'POST',
            body,
          },
        ),
      sourceImportStatus: (jobId: string): Promise<ApiResult<ChecklistSourceImportJob>> =>
        api.request<ChecklistSourceImportJob>('/checklists/source-ingest', {
          method: 'POST',
          body: { operation: 'IMPORT_STATUS', jobId },
        }),
      sourceBatchIngest: (body: {
        key: string;
        fingerprint: string;
      }): Promise<ApiResult<ChecklistSourceImportBatch>> =>
        api.request<ChecklistSourceImportBatch>('/checklists/source-ingest', {
          method: 'POST',
          body: { operation: 'BATCH_IMPORT', ...body },
        }),
      sourceBatchStatus: (batchId: string): Promise<ApiResult<ChecklistSourceImportBatch>> =>
        api.request<ChecklistSourceImportBatch>('/checklists/source-ingest', {
          method: 'POST',
          body: { operation: 'BATCH_STATUS', batchId },
        }),
    }),
    [api],
  );
}
